import { saveTranslations } from '../../ducks/translations.duck';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { completeTask, TASK_NAMES } from '../../ducks/voucherify.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { logError } from '../../util/log';
import { oneSignalAddTags } from '../../util/mobile/oneSignal';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.ProfileSettingsPage} */
const initialState = {
  image: null,
  uploadInProgress: false,
  uploadImageError: null,
  updateInProgress: false,
  updateProfileError: null,
};

/**
 *
 * @param state
 * @param action
 */
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {

      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    default:
      return state;
  }
}

// ================ Selectors ================ //

/**
 * @param {import('@vansite/ts-sharetribe-flex-sdk').CurrentUser } currentUser
 * @returns {boolean}
 */
export const checkForProfileCompletion = (currentUser) => {
  const { attributes } = currentUser;
  const { profile } = attributes;
  const { firstName, lastName, bio, privateData, publicData } = profile;
  const { birthDate } = privateData;
  const { spokenLanguages, travelDetails, favoriteCountries, campingExperience, campingTime } =
    publicData;

  return (
    !!firstName &&
    !!lastName &&
    !!bio &&
    !!birthDate &&
    !!Array.isArray(spokenLanguages) &&
    spokenLanguages.length > 0 &&
    !!travelDetails &&
    !!travelDetails.vehicle &&
    !!travelDetails.animalCompanion &&
    !!travelDetails.travelingWith &&
    !!Array.isArray(favoriteCountries) &&
    favoriteCountries.length > 0 &&
    !!campingExperience &&
    !!campingTime
  );
};

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = (params) => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = (result) => ({
  type: UPLOAD_IMAGE_SUCCESS,
  payload: result.data,
});
export const uploadImageError = (error) => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = (params) => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = (result) => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = (error) => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
/**
 *
 * @param actionPayload
 */
export const uploadImage = (actionPayload) => (dispatch, getState, sdk) => {
  const id = actionPayload.id;
  dispatch(uploadImageRequest(actionPayload));

  const bodyParams = {
    image: actionPayload.file,
  };
  const queryParams = {
    expand: true,
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.images
    .upload(bodyParams, queryParams)
    .then((resp) => {
      const uploadedImage = resp.data.data;
      dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
    })
    .catch((e) => {
      logError(e, 'profile-settings-upload-image', { id });
      dispatch(uploadImageError({ id, error: storableError(e) }));
    });
};

/**
 * @type {(actionPayload: any) => import('../../../types/vansite').AppThunkAction<Promise<void>>}
 */
export const updateProfile = (actionPayload) => async (dispatch, getState, sdk) => {
  dispatch(updateProfileRequest());

  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  const { currentUser } = getState().user;
  if (actionPayload.bio) {
    dispatch(
      saveTranslations({
        type: 'user',
        id: currentUser.id.uuid,
        content: { bio: actionPayload.bio.trim() },
      }),
    );
  } else {
    dispatch(
      saveTranslations({
        type: 'user',
        id: currentUser.id.uuid,
        content: { bio: '' },
      }),
    );
  }

  try {
    const response = await sdk.currentUser.updateProfile(actionPayload, queryParams);
    dispatch(updateProfileSuccess(response));

    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }
    /** @type {import('@vansite/ts-sharetribe-flex-sdk').CurrentUser} */
    const currentUser = entities[0];

    if (checkForProfileCompletion(currentUser)) {
      dispatch(completeTask(TASK_NAMES.PROFILE_FILLED_OUT));
    }

    oneSignalAddTags({ firstName: actionPayload.firstName, lastName: actionPayload.lastName });

    // Update current user in state.user.currentUser through user.duck.js
    dispatch(currentUserShowSuccess(currentUser));
  } catch (e) {
    logError(e, 'profile-settings-update-profile', { actionPayload });
    dispatch(updateProfileError(storableError(e)));
  }
};
